import styled from '@emotion/styled';
import React, { Suspense, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Lottie from 'react-lottie-player';
import { duration } from 'moment';

import successAnimation from 'src/public/animations/colorful_confetti.json';
import img from 'src/public/img/tada.png';
import { CoachNode } from 'src/generated/webapp_gql';
import { ProviderType } from 'src/modules/user/models/user';
import { Button } from 'src/shared/components/Button';
import { Modal } from 'src/shared/components/Modal';
import { color, font, mediaQueries } from 'src/shared/styles/variables';
import { useAnalytics } from '@modernhealth/web-analytics';
import { config } from 'src/config/config';
import { FormattedSessionDateTimeRange } from 'src/modules/videoSessions/DateTimeFormatter';

import { DEFAULT_LATE_CANCELLATION_THRESHOLD, SessionSlot } from '../models/videoSessions';

import { AddToCalendarModal, Calendar } from './AddToCalendarModal';

const StyledModal = styled(Modal)<StyledModalProps>(props => ({
    '.ant-modal-content': {
        borderRadius: props.fullscreen ? 0 : 7.5,
        height: props.fullscreen ? '100vh' : 'auto',
        display: 'flex',
        flexFlow: 'column',
        justifyContent: 'center',
        alignItems: 'center',

        '.ant-modal-body': {
            padding: 0,
        },
    },
}));

const Wrapper = styled.div<{ isSessionBooked: boolean }>(
    {
        padding: '63px 0 45px',
        display: 'flex',
        margin: '0 auto',
        flexDirection: 'column',
        alignItems: 'center',
        [mediaQueries.md]: {
            padding: '36px 0 26px',
        },
    },
    props => ({
        maxWidth: props.isSessionBooked ? 710 : 560,
    }),
);

const SuccessAnimation = styled(Lottie)({
    width: 400,
    position: 'absolute',
});

const HeaderText = styled.h3(
    {
        color: color.ebonyClay,
        textAlign: 'center',
        marginBottom: 0,
    },
    font.black(font.size.extraLarge),
);

const DateText = styled.p(
    {
        color: color.blueBayoux,
        textAlign: 'center',
        opacity: 0.8,
        marginTop: 10,
    },
    font.heavy(font.size.large),
);

const SessionInfo = styled.p({
    color: color.blueBayoux,
    textAlign: 'center',
    marginTop: 15,
    marginBottom: 20,
    whiteSpace: 'break-spaces',
    ...font.roman(font.size.medium),
});

const Text = styled.p(
    {
        textAlign: 'center',
        color: color.steelGray,
        lineHeight: '26px',
        marginTop: 22,
    },
    font.book(font.size.medium),
);

const TextColored = styled.span(
    {
        textAlign: 'center',
        color: color.royalBlue,
    },
    font.heavy(font.size.medium),
);

const ImageWrapper = styled.div({
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    overflowY: 'hidden',
    width: '100%',
    minHeight: '200px',
    marginBottom: 40,
});

const ButtonsWrapper = styled.div({
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    marginTop: 22,
});

interface StyledModalProps {
    fullscreen: boolean;
}

export enum ScheduleSessionConfirmationModalTestID {
    Container = 'ScheduleSessionConfirmationModalTestID.Container',
}

export interface ScheduleSessionConfirmationModalProps {
    visible: boolean;
    loading: boolean;
    sessionBooked: boolean;
    sessionSlot: SessionSlot;
    onboardingMode?: boolean;
    onConfirm?: () => void; // required if sessionBooked === false
    onClose?: () => void; // required if sessionBooked === false
    onSkip: () => void;
    onExit: () => void;
    onAddToCalendar: (calendar: Calendar) => void;
    calendars: Calendar[];
    onSessionBookedConfirm: () => void;
    provider: CoachNode;
    closable?: boolean;
}

export const ScheduleSessionConfirmationModal: React.FC<ScheduleSessionConfirmationModalProps> = ({
    visible,
    onConfirm = () => {},
    onClose = () => {},
    onExit,
    onSkip,
    onboardingMode = false,
    sessionSlot,
    loading,
    sessionBooked,
    onAddToCalendar,
    calendars,
    onSessionBookedConfirm,
    provider,
    closable = true,
}) => {
    const { t } = useTranslation();
    const analytics = useAnalytics();
    const [addToCalendarModalVisible, setAddToCalendarModalVisible] = useState(false);

    const lateCancellationThreshold =
        (provider?.lateCancellationThreshold || DEFAULT_LATE_CANCELLATION_THRESHOLD) / 60 / 60;

    const getSessionDuration = () => {
        return duration(sessionSlot.end.diff(sessionSlot.start)).asMinutes();
    };

    const providerType = provider.providerType || ProviderType.COACH;
    const isPsychiatry = providerType === ProviderType.PSYCHIATRIST;
    const isPsychiatryInitialEvaluation = getSessionDuration() === 60;

    const getHeaderKey = () => {
        const sessionTypeHeaders: { [key: string]: string } = {
            [ProviderType.THERAPIST]: 'therapy',
            [ProviderType.COACH]: 'coaching',
            [ProviderType.PSYCHIATRIST]: `psychiatry.${
                isPsychiatryInitialEvaluation ? 'initial_evaluation' : 'follow_up'
            }`,
        };

        return `videoSessions:session_confirmation_modal.session_booked.${sessionTypeHeaders[providerType]}`;
    };

    const getDescriptionKey = () => {
        const sessionTypeDescriptions: { [key: string]: string } = {
            [ProviderType.THERAPIST]: 'therapist',
            [ProviderType.COACH]: 'coach',
            [ProviderType.PSYCHIATRIST]: 'psychiatrist',
        };

        return `videoSessions:session_confirmation_modal.session_info.${sessionTypeDescriptions[providerType]}`;
    };

    const getConfirmationButtonKey = () => {
        if (isPsychiatry) return 'videoSessions:session_confirmation_modal.back_to_dashboard';

        return 'videoSessions:session_confirmation_modal.book_another_session';
    };

    return (
        <div data-testid={ScheduleSessionConfirmationModalTestID.Container}>
            <AddToCalendarModal
                visible={addToCalendarModalVisible}
                onAddToCalendar={calendar => {
                    onAddToCalendar(calendar);
                    setAddToCalendarModalVisible(false);
                }}
                onCancel={() => {
                    setAddToCalendarModalVisible(false);
                }}
                calendars={calendars}
            />
            <StyledModal
                visible={visible}
                footer={null}
                width={sessionBooked ? '100%' : 760}
                onCancel={onExit}
                closable={closable}
                centered
                css={{ height: sessionBooked ? '100vh' : 'auto' }}
                fullscreen={sessionBooked}
            >
                <Wrapper
                    isSessionBooked={sessionBooked}
                    data-testid="schedule-session-confirmation-modal"
                >
                    {sessionBooked ? (
                        <>
                            <ImageWrapper>
                                <Suspense fallback={<img src={img} alt="" />}>
                                    <SuccessAnimation
                                        animationData={successAnimation}
                                        speed={0.6}
                                        play
                                        loop
                                    />
                                    <img src={img} alt="" />
                                </Suspense>
                            </ImageWrapper>

                            <HeaderText>{t(getHeaderKey())}</HeaderText>
                            <DateText>
                                <FormattedSessionDateTimeRange
                                    start={sessionSlot.start.toDate()}
                                    end={sessionSlot.end.toDate()}
                                />
                            </DateText>
                            <SessionInfo>
                                <Trans
                                    t={t}
                                    i18nKey={getDescriptionKey()}
                                    components={{
                                        'ios-link': <a href={config.iOSAppStoreURL}>iOS</a>,
                                        'android-link': (
                                            <a href={config.AndroidGooglePlayStoreURL}>Android</a>
                                        ),
                                    }}
                                />
                            </SessionInfo>
                            <ButtonsWrapper>
                                <Button
                                    type="white"
                                    onClick={() => {
                                        analytics.trackClick('Book another session');
                                        return onSessionBookedConfirm();
                                    }}
                                    noHorizontalPadding
                                    width="normal"
                                >
                                    {t(getConfirmationButtonKey())}
                                </Button>
                                <Button
                                    type="accept"
                                    onClick={() => {
                                        const buttonText = t(
                                            'videoSessions:session_confirmation_modal.add_to_calendar',
                                        );

                                        analytics.trackClick('Opened Add to Calendar Modal', {
                                            button_copy: buttonText,
                                        });
                                        setAddToCalendarModalVisible(true);
                                    }}
                                    noHorizontalPadding
                                    width="normal"
                                    marginLeft={18}
                                >
                                    {t('videoSessions:session_confirmation_modal.add_to_calendar')}
                                </Button>
                            </ButtonsWrapper>
                            {!isPsychiatry &&
                                (onboardingMode ? (
                                    <Button marginTop={15} type="noBackground" onClick={onSkip}>
                                        {t('common.skip')}
                                    </Button>
                                ) : (
                                    <Button onClick={onExit} type="noBackground" marginTop={15}>
                                        {/* TODO: Update this copy to "Continue" when showIntakeForm is turned on */}
                                        {t(
                                            'videoSessions:session_confirmation_modal.back_to_dashboard',
                                        )}
                                    </Button>
                                ))}
                        </>
                    ) : (
                        <>
                            <HeaderText>
                                <FormattedSessionDateTimeRange
                                    start={sessionSlot.start.toDate()}
                                    end={sessionSlot.end.toDate()}
                                />
                            </HeaderText>
                            {isPsychiatry ? (
                                <>
                                    <Text>
                                        {t(
                                            'videoSessions:session_confirmation_modal.psychiatry.description.part1',
                                        )}
                                    </Text>
                                    <Text>
                                        <Trans
                                            t={t}
                                            i18nKey="videoSessions:session_confirmation_modal.psychiatry.description.part2"
                                            components={{ bold: <TextColored /> }}
                                        />
                                    </Text>
                                </>
                            ) : (
                                <Text>
                                    <Trans
                                        t={t}
                                        ns="videoSessions"
                                        i18nKey="session_confirmation_modal.description"
                                        values={{ lateCancellationThreshold }}
                                        components={{
                                            highlight: <TextColored />,
                                        }}
                                    />
                                </Text>
                            )}
                            <ButtonsWrapper>
                                <Button
                                    onClick={onClose}
                                    type="white"
                                    noHorizontalPadding
                                    width="normal"
                                >
                                    {t('videoSessions:session_confirmation_modal.back_to_calendar')}
                                </Button>
                                <Button
                                    onClick={onConfirm}
                                    loading={loading}
                                    noHorizontalPadding
                                    marginLeft={18}
                                    width="normal"
                                >
                                    {t('videoSessions:session_confirmation_modal.confirm')}
                                </Button>
                            </ButtonsWrapper>
                        </>
                    )}
                </Wrapper>
            </StyledModal>
        </div>
    );
};
